import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import Loader from '__components/Kizen/Loader';
import { useToast } from '__components/ToastProvider';
import { getOriginalError } from '__services/AxiosService';
import { getErrorMessage } from '__hooks/useErrors';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { knowledgeCatalogQuery } from 'query/knowledge';
import KnowledgeService from 'services/KnowledgeService';
import type { KnowledgeCatalogSharingSettings } from '@kizen/api/knowledge';
import useCan from 'hooks/useCan';
import { SettingsContentCard } from '__components/Card/Card';
import PermissionSettings from '__components/Sharing/PermissionSettings';
import { buildSharingSettingsForAPI } from 'utility/sharing';
import { useSetSaveAction } from '../ToolbarContext';

const StyledSettingsContentCard = styled(SettingsContentCard)`
  padding-top: 20px;
  margin-top: 20px;
`;

export const CatalogTeamSharingSettings = () => {
  const { t } = useTranslation();
  const { catalogId } = useParams<{ catalogId: string }>();
  const [showToast, toastVariant] = useToast();
  const queryClient = useQueryClient();
  const setSaveAction = useSetSaveAction();

  const canWriteSection = useCan({
    edit: 'sections',
    for: 'settings_section.knowledgebase_setting',
  });

  const queryOptions = useMemo(
    () =>
      knowledgeCatalogQuery(catalogId, {
        placeholderData: keepPreviousData,
        staleTime: 0,
        enabled: !!catalogId,
      }),
    [catalogId]
  );

  const { data: catalog, isLoading } = useQuery(queryOptions);

  const canWrite = canWriteSection;

  const [permissions, setPermissions] = useState<any>(null);
  const [isDirty, setIsDirty] = useState(false);

  const handlePermissionsChange = useCallback((state: any, dirty: boolean) => {
    setPermissions(state);
    if (dirty) setIsDirty(true);
  }, []);

  const { mutate: saveSharingSettings, isPending: isSaving } = useMutation({
    mutationFn: () =>
      KnowledgeService.updateKnowledgeCatalog(catalogId, {
        sharing_settings: (
          buildSharingSettingsForAPI(permissions) as {
            sharing_settings?: KnowledgeCatalogSharingSettings;
          }
        )?.sharing_settings,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
      setIsDirty(false);
      showToast({
        message: t('Sharing settings saved successfully'),
        variant: toastVariant.SUCCESS,
      });
    },
    onError: (error) => {
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ||
          t('Failed to save sharing settings'),
        variant: toastVariant.FAILURE,
      });
    },
  });

  const handleSave = useCallback(() => {
    saveSharingSettings();
  }, [saveSharingSettings]);

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    if (!canWrite) {
      setSaveAction(null);
      return () => setSaveAction(null);
    }
    setSaveAction({
      onSave: () => handleSaveRef.current(),
      disabled: !isDirty || isSaving,
    });
    return () => setSaveAction(null);
  }, [canWrite, isDirty, isSaving, setSaveAction]);

  return (
    <Loader loading={isLoading}>
      <StyledSettingsContentCard>
        <PermissionSettings
          onChange={handlePermissionsChange}
          existing={catalog?.sharing_settings ?? {}}
          loading={isLoading}
          disableFocus
          removeTopPadding
        />
      </StyledSettingsContentCard>
    </Loader>
  );
};
